import PageHeader from "@/components/PageHeader";
import { Reveal, Overline } from "@/components/Reveal";
import { Link } from "react-router-dom";
import {
  Check,
  Headphones,
  Mic,
  Crown,
  Gem,
  Camera,
  Sparkles,
  Cloud,
  Lightbulb,
  Zap,
  Speaker,
  Drum,
  Users,
  Clock,
  Star,
} from "lucide-react";

const packages = [
  {
    id: "essential",
    icon: Headphones,
    name: ["Dream", "Essential"],
    formValue: "Dream Essential",
    tagline: "Everything you need for a great celebration.",
    featured: false,
    cta: "Check Availability",
    features: [
      "Professional DJ",
      "Professional Sound System",
      "Wireless Microphone",
      "Dance Floor Lighting",
      "Custom Music Planning",
      "Guest Song Requests",
      "Event Setup & Breakdown",
    ],
    bestFor: "Birthdays, graduations, private parties and smaller celebrations.",
  },
  {
    id: "signature",
    icon: Mic,
    name: ["Dream", "Signature"],
    formValue: "Dream Signature",
    tagline: "A complete DJ and hosting experience designed to keep your event moving.",
    featured: true,
    cta: "Build Your Experience",
    features: [
      "Everything in Essential",
      "Professional MC",
      "Enhanced Sound Setup",
      "Upgraded Dance Floor Lighting",
      "Venue Uplighting",
      "Multiple Wireless Microphones",
      "Custom Introductions & Announcements",
      "Event Timeline Coordination",
      "Music Cues for Key Moments",
    ],
    bestFor: "Weddings, engagements, anniversaries, Sweet 16s and larger celebrations.",
  },
  {
    id: "premier",
    icon: Crown,
    name: ["Dream", "Premier"],
    formValue: "Dream Premier",
    tagline: "Our elevated entertainment experience for events that deserve more than the ordinary.",
    featured: false,
    cta: "Request Your Custom Package",
    features: [
      "Everything in Signature",
      "Premium Lighting Design",
      "Moving-Head Lighting",
      "360° Photo Booth",
      "Special Entrance Effects",
      "Enhanced Room Atmosphere",
      "Customized Lighting & Music Moments",
      "Expanded Event Coordination",
    ],
    bestFor: "Weddings, receptions, large celebrations and upscale events.",
  },
  {
    id: "wedding",
    icon: Gem,
    name: ["Dream Wedding", "Experience"],
    formValue: "Dream Wedding Experience",
    tagline: "From your first entrance to the final song, we bring every part of your celebration together.",
    featured: false,
    cta: "Plan Your Wedding",
    features: [
      "Professional DJ + MC",
      "Ceremony Audio",
      "Cocktail Hour Audio",
      "Reception Entertainment",
      "Premium Sound",
      "Venue Uplighting",
      "Moving-Head Lighting",
      "Baraat Entertainment & Sound",
      "First Dance & Special Moment Coordination",
      "360° Photo Booth",
      "Special Effects",
      "Music Planning",
      "Event Timeline Coordination",
      "Dhol and Live Percussion Options",
    ],
    bestFor: "Full-scale weddings, South Asian weddings and multi-event celebrations.",
  },
];

const addons = [
  { id: "photo-booth", icon: Camera, title: "360° Photo Booth", text: "Give your guests an interactive experience with shareable videos and memories from your celebration." },
  { id: "cold-sparks", icon: Sparkles, title: "Cold Sparks", text: "Create unforgettable entrances, first dances and highlight moments with dramatic indoor-safe spark effects." },
  { id: "clouds", icon: Cloud, title: "Dancing on the Clouds", text: "Transform your first dance or special moment with a beautiful low-lying fog effect." },
  { id: "uplighting", icon: Lightbulb, title: "Venue Uplighting", text: "Add color and atmosphere throughout your venue with customizable uplighting." },
  { id: "intelligent-lighting", icon: Zap, title: "Intelligent Lighting", text: "Moving-head lighting and enhanced effects designed to transform your dance floor." },
  { id: "additional-sound", icon: Speaker, title: "Additional Sound", text: "Additional speakers and audio setups for ceremonies, cocktail hours, separate rooms or larger venues." },
  { id: "ceremony-audio", icon: Mic, title: "Ceremony & Cocktail Hour Audio", text: "Dedicated sound and microphones for ceremonies, speeches, cocktail hours and other portions of your event." },
  { id: "dhol", icon: Drum, title: "Dhol & Live Percussion", text: "Add live musical energy and culture to entrances, dancing and special moments." },
  { id: "baraat", icon: Users, title: "Baraat Experience", text: "High-energy Baraat sound, music and coordination designed to make your grand entrance unforgettable." },
  { id: "extra-time", icon: Clock, title: "Additional Event Time", text: "Keep the celebration going beyond your originally scheduled entertainment time." },
  { id: "custom", icon: Star, title: "Custom Enhancements", text: "Have something unique in mind? We can customize lighting, sound, entertainment and special moments around your vision." },
];

export default function Packages() {
  return (
    <main data-testid="packages-page">
      <PageHeader
        center
        overline="Packages"
        title="Entertainment"
        accent="packages."
        description="Custom experiences. Unforgettable moments. Every event is quoted personally. Tell us about yours and we'll build a custom quote within 24 hours."
      />

      {/* PACKAGES */}
      <section className="max-w-[90rem] mx-auto px-6 sm:px-12 lg:px-16 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {packages.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <div
                data-testid={`package-card-${p.id}`}
                className={`relative rounded-3xl p-8 h-full flex flex-col glass text-center transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 ${
                  p.featured
                    ? "border-primary/60 shadow-[0_0_60px_rgba(212,175,55,0.15)] hover:shadow-[0_0_80px_rgba(255,0,127,0.2)]"
                    : "hover:border-primary/30 hover:shadow-[0_0_60px_rgba(255,0,127,0.12)]"
                }`}
              >
                {p.featured && (
                  <span
                    data-testid="package-featured-badge"
                    className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-[10px] tracking-[0.3em] uppercase px-5 py-1.5 whitespace-nowrap"
                  >
                    Most Popular
                  </span>
                )}
                <p.icon size={40} strokeWidth={1.25} className="text-primary mx-auto" />
                <h2 className="mt-5 leading-tight">
                  <span className="block text-sm tracking-[0.25em] uppercase text-foreground/90">
                    {p.name[0]}
                  </span>
                  <span className="block font-serif text-3xl sm:text-4xl text-primary">
                    {p.name[1]}
                  </span>
                </h2>
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                  {p.tagline}
                </p>
                <div className="my-6 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
                <ul className="space-y-3 text-left flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm text-foreground/80 leading-snug">
                      <Check size={15} className="text-primary shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-primary">Best For:</p>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.bestFor}</p>
                </div>
                <Link
                  to={`/booking?package=${encodeURIComponent(p.formValue)}`}
                  data-testid={`package-cta-${p.id}`}
                  className={`mt-6 block rounded-full px-6 py-3.5 text-xs font-medium tracking-[0.2em] uppercase transition-[transform,box-shadow,background-color,color,border-color] duration-300 hover:scale-[1.03] ${
                    p.featured
                      ? "bg-primary text-primary-foreground hover:shadow-[0_0_40px_rgba(255,0,127,0.4)]"
                      : "border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                  }`}
                >
                  {p.cta}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="max-w-[90rem] mx-auto px-6 sm:px-12 lg:px-16 pb-24">
        <Reveal className="text-center mb-14">
          <Overline>Add-Ons &amp; Enhancements</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6">
            Enhance your <em className="text-primary">experience.</em>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {addons.map((a, i) => (
            <Reveal key={a.id} delay={(i % 4) * 0.08} className="h-full">
              <div
                data-testid={`addon-card-${a.id}`}
                className="glass rounded-2xl p-7 h-full text-center transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-primary/30 hover:shadow-[0_0_40px_rgba(212,175,55,0.1)]"
              >
                <a.icon size={34} strokeWidth={1.25} className="text-primary mx-auto" />
                <h3 className="text-xs tracking-[0.25em] uppercase mt-4">{a.title}</h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.08),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-12 pb-24 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Your event. Your vision. <em className="text-primary">Your dream.</em>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Every celebration is different. Tell us about your event and
              we&rsquo;ll create an entertainment experience customized around
              your venue, guests and vision.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/booking"
                data-testid="packages-bottom-build-btn"
                className="rounded-full bg-primary text-primary-foreground px-10 py-4 text-sm font-medium tracking-widest uppercase transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(255,0,127,0.4)]"
              >
                Build Your Dream Event
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
